import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Brain, Activity, Scale, HeartPulse, Stethoscope, ArrowRight, FileCheck } from 'lucide-react';
export default function Services() {
  const services = [{
    title: 'Mental Capacity Assessments UK',
    description: "Meticulous evaluations of an individual's capacity to make specific decisions, adhering to the Mental Capacity Act 2005. These assessments are vital for upholding individual rights and ensuring legal decisions are based on a profound understanding of mental capacity.",
    icon: <Activity className="h-12 w-12" data-id="element-1103" />,
    link: '/services/mental-capacity'
  }, {
    title: 'PTSD & Trauma Psychiatric Evaluations',
    description: "Expert assessments for Post-Traumatic Stress Disorder (PTSD) and Complex PTSD. Dr. Syed's reports adhere to international standards, providing sensitive and comprehensive evaluations of psychological distress stemming from traumatic experiences.",
    icon: <HeartPulse className="h-12 w-12" data-id="element-1104" />,
    link: '/services/trauma-ptsd'
  }, {
    title: 'ADHD & Autism Medico-Legal Assessments',
    description: "Comprehensive assessments for Attention-Deficit/Hyperactivity Disorder (ADHD) and Autism Spectrum Disorder (ASD). These insights are crucial in understanding how neurodevelopmental conditions may impact an individual's legal context or capacity.",
    icon: <Brain className="h-12 w-12" data-id="element-1105" />,
    link: '/services/neurodevelopmental'
  }, {
    title: 'Forensic Psychiatric Assessments UK',
    description: 'Critical insights into mental state within criminal justice contexts, including assessments regarding fitness to plead and stand trial, alongside thorough risk assessments concerning violence, self-harm, and self-neglect.',
    icon: <Scale className="h-12 w-12" data-id="element-1106" />,
    link: '/services/forensic-risk'
  }, {
    title: 'Psychiatric Reports for Courts & Tribunals',
    description: 'Expert psychiatric opinions tailored for diverse legal settings, including Immigration and Asylum Tribunals, Criminal Courts, Family Courts, Employment Tribunals, Civil Claims, and Mental Health Review Tribunals.',
    icon: <Stethoscope className="h-12 w-12" data-id="element-1107" />,
    link: '/services/specialist'
  }, {
    title: 'Clinical Negligence Psychiatric Assessments',
    description: 'Independent psychiatric assessments for clinical negligence cases, including causation, psychiatric injury, prognosis, and expert witness reporting.',
    icon: <FileCheck className="h-12 w-12" data-id="element-1108" />,
    link: '/services/clinical-negligence'
  }];
  return <main className="min-h-screen bg-white" data-id="element-1109">
      <Helmet data-id="element-1110">
        <title data-id="element-1111">Psychiatric Medico-Legal Services UK | Expert Reports</title>
        <meta name="description" content="Specialist psychiatric medico-legal services including mental capacity, PTSD, forensic risk, ADHD, clinical negligence, and expert witness reports for UK courts." data-id="element-1112" />
        <meta name="keywords" content="Psychiatric Medico-Legal Services, Expert Witness Psychiatrist UK, Psychiatric Expert Witness Reports, Independent Psychiatric Reports UK, Forensic Psychiatric Assessments UK, Mental Capacity Assessments UK, ADHD & Autism Medico-Legal Assessments, PTSD & Trauma Psychiatric Evaluations, Psychiatric Reports for Courts & Tribunals, Psychiatric Expert for Solicitors and Barristers" data-id="element-1113" />
        <script type="application/ld+json" data-id="element-1114">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Psychiatric Medico-Legal Services UK",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Psychiatric Expert Witness Services",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert Psychiatric Reports and Assessments"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1115">
        <div className="container mx-auto px-4" data-id="element-1116">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1117">
            Psychiatric Medico-Legal Services UK | Expert Reports
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1118">
            Dr. Syed provides comprehensive Medico-Legal services for
            solicitors, barristers, and courts, guided by clarity, compassion,
            and expertise.
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1119">
        <div className="container mx-auto px-4" data-id="element-1120">
          <div className="max-w-3xl mx-auto mb-12" data-id="element-1121">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1122">
              At Dr. Imtiaz Syed's practice, we are dedicated to providing
              precise, impartial, and robust psychiatric expertise to legal
              professionals across the UK. We understand the critical role that
              expert insights play in complex legal proceedings, and our
              services are meticulously designed to deliver clarity and
              confidence to your cases.
            </p>
            <p className="text-slate-dark text-lg mb-6" data-id="element-1123">
              Dr. Syed offers a comprehensive suite of medico-legal psychiatric
              assessments and expert witness services, all delivered with the
              guiding principles of Clarity, Compassion, and Expertise. Each
              assessment is conducted with the utmost care, ensuring
              thoroughness and sensitivity, leading to reports that are not only
              scientifically sound but also practical and directly applicable to
              legal contexts.
            </p>
            <p className="text-slate-dark text-lg" data-id="element-1124">
              Explore our key areas of specialisation:
            </p>
          </div>
          {/* Services */}
          <div className="space-y-12 mb-16" data-id="element-1125">
            {services.map((service, index) => <div key={index} className="bg-white rounded-lg shadow-md p-8 border-l-4 border-primary" data-id="element-1126">
                <div className="flex flex-col md:flex-row gap-6" data-id="element-1127">
                  <div className="text-primary md:w-24 flex justify-center" data-id="element-1128">
                    {service.icon}
                  </div>
                  <div className="flex-1" data-id="element-1129">
                    <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1130">
                      {service.title}
                    </h2>
                    <p className="text-slate-dark mb-6" data-id="element-1131">
                      {service.description}
                    </p>
                    <Link to={service.link} className="inline-flex items-center text-primary font-medium hover:text-secondary transition-colors" data-id="element-1132">
                      More on {service.title}{' '}
                      <ArrowRight className="ml-1 h-4 w-4" data-id="element-1133" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>
          {/* Why Choose Section */}
          <div className="bg-gray-50 rounded-lg p-8 shadow-md" data-id="element-1134">
            <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1135">
              Why Choose Dr. Imtiaz Syed for Expert Witness Services?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1136">
              Our commitment is to deliver expert medico-legal reports that are:
            </p>
            <ul className="space-y-3 mb-6 text-slate-dark" data-id="element-1137">
              <li className="flex items-start" data-id="element-1138">
                <span className="text-secondary font-bold mr-2" data-id="element-1139">•</span>
                <span data-id="element-1140">
                  <strong data-id="element-1141">Robust & Objective:</strong> Clinically sound,
                  evidence-based, and impartial.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1142">
                <span className="text-secondary font-bold mr-2" data-id="element-1143">•</span>
                <span data-id="element-1144">
                  <strong data-id="element-1145">Clear & Concise:</strong> Jargon-free and directly
                  relevant to legal questions.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1146">
                <span className="text-secondary font-bold mr-2" data-id="element-1147">•</span>
                <span data-id="element-1148">
                  <strong data-id="element-1149">Timely & Responsive:</strong> Delivered efficiently to
                  meet court deadlines.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1150">
                <span className="text-secondary font-bold mr-2" data-id="element-1151">•</span>
                <span data-id="element-1152">
                  <strong data-id="element-1153">Ethical & Confidential:</strong> Adhering to the
                  highest professional standards.
                </span>
              </li>
            </ul>
            <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1154">
              Ready to Instruct Dr. Syed?
            </h3>
            <p className="text-slate-dark mb-4" data-id="element-1155">
              We invite legal professionals to explore these service areas in
              more detail via the dedicated subpages or to contact us directly
              to discuss your specific case requirements.
            </p>
            <div className="flex items-center" data-id="element-1156">
              <span className="mr-2 text-slate-dark" data-id="element-1157">Contact Us:</span>
              <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1158">
                enquiries@medicolegal-psychiatry.co.uk
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>;
}